import React, { useEffect, useState } from 'react';
import { Backdrop, Box, CircularProgress, Grid } from '@mui/material';
import BasicInfo from '../../_common/BasicInfo/BasicInfo';
import QuestionComponent from './components/Question';
import FooterButton from './components/FooterButton';
import PFRCodeTable from './components/PFRCodeTable';
import ResultInformation from './components/ResultInformation';
import DisplayDocuments from './components/UploadDocuments/DisplayImage';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../stores/store';
import {
  fetchConfirmedInspection,
  setDocumentComment,
  setDocumentPath,
  setFilePath,
  updateConfirmedInspection,
} from '../../stores/confirmedInspection/slice';
import {
  ACCEPT_TYPE_IMAGE,
  CHUNK_SIZE,
  DIAGNOSTIC_CONTAINER_NAME,
  HISTORY_STATUSES,
  HISTORY_TYPE,
  INSPECTION_STATUSES,
} from '../../_constants/inspectionConst';
import { useNavigate } from 'react-router-dom';
import { PutConfirmedInspection, PostBlockList } from '../../services/apiTypes';
import {
  deleteFiles,
  uploadBlockData,
  uploadBlockList,
} from '../../stores/confirmedInspection/api';
import AcceptUploadDocuments from './components/UploadDocuments/AcceptUploadFile';
import UploadDocuments from './components/UploadDocuments/DisplayDocument';
import { getBlobData } from '../../services/api/BlobData';
import { Buffer } from 'buffer';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import { ANSWER_VALUE, INSPEX_PATHNAME } from '../../_constants/Const';
import { LOADING_BACKDROP_STYLE } from '../../_constants/styleConst';
import { isRejected } from '@reduxjs/toolkit';
import {
  isGetBlobData,
  isGetConfirmedInspection,
} from '../../_utils/typeGuardUtils';
import { isEmptyData } from '../../_utils/utils';
import { CustomError } from '../../services/CustomError';
import { API_CODE } from '../../_constants/apiConst';
import { useTranslation } from 'react-i18next';
import useUpdateStatus from '../../hooks/useUpdateStatus';
import useSaveHistory from '../../hooks/useSaveHistory';
import { MaxLength } from '../../_constants/maxLengthConst';
import useResetStore from '../../hooks/useResetStore';

const gridStyle = {
  bgcolor: '#FFF',
  margin: '20px 0px',
  borderRadius: '4px',
  position: 'relative',
  '&:hover .hover-button2': {
    display: 'flex',
  },
};

/** 確定診断画面 */
const ConfirmedInspection: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { error, showApiError, showTypeError, showClientError, hideError } =
    useErrorModal();
  const updateStatus = useUpdateStatus();
  const saveHistory = useSaveHistory();
  const { resetInspectionStore } = useResetStore();
  const { confirmedInspection } = useSelector(
    (state: RootState) => state.confirmedInspection,
  );
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );

  const [isLoading, setIsLoading] = useState(false);
  // 顧客指摘があるかどうかのトグル
  const [isReported, setIsReported] = useState(true);

  const [files, setFiles] = useState<
    [
      {
        id: number;
        fileName?: string;
        file?: File;
        isImage: boolean;
      },
      {
        id: number;
        fileName?: string;
        file?: File;
        isImage: boolean;
      },
      {
        id: number;
        fileName?: string;
        file?: File;
        isImage: boolean;
      },
      {
        id: number;
        fileName?: string;
        file?: File;
        isImage: boolean;
      },
    ]
  >([
    // idはDocumentData.csへの変換のために使用
    { id: 0, isImage: true },
    { id: 1, isImage: true },
    { id: 0, isImage: false },
    { id: 1, isImage: false },
  ]);

  useEffect(() => {
    if (!repairRequest) return;

    (async () => {
      const response = await dispatch(
        fetchConfirmedInspection({ rmaNo: repairRequest.rmaNo }),
      );

      if (isRejected(response)) {
        showApiError(response.payload, API_CODE.CONFIRMED_INSPECTION, () =>
          navigate(INSPEX_PATHNAME.RMA_SEARCH),
        );
        return;
      }
      if (!isGetConfirmedInspection(response.payload)) {
        showTypeError(API_CODE.CONFIRMED_INSPECTION);
        return;
      }

      if (response.payload.documents.length > 0) {
        try {
          setIsLoading(true);
          // 1ファイルずつ取得する
          for (const data of response.payload.documents) {
            if (!isEmptyData(data.imagePath)) {
              // imagePath === "修理依頼番号/image0(1)/ファイル名"
              const slashIndex = data.imagePath.lastIndexOf('/');
              const folderPath = data.imagePath.substring(0, slashIndex + 1);
              await getBlobDataSingleAsync(folderPath);
            }
            if (!isEmptyData(data.documentPath)) {
              // documentPath === "修理依頼番号/document0(1)/ファイル名"
              const slashIndex = data.documentPath.lastIndexOf('/');
              const folderPath = data.documentPath.substring(0, slashIndex + 1);
              await getBlobDataSingleAsync(folderPath);
            }
          }
        } catch (error) {
          showApiError(error, API_CODE.BLOB_DATA);
        } finally {
          setIsLoading(false);
        }
      }
    })();
  }, [repairRequest?.rmaNo]);

  const getBlobDataSingleAsync = async (folderPath: string) => {
    try {
      const blobData = await getBlobData({
        containerName: DIAGNOSTIC_CONTAINER_NAME,
        path: folderPath,
      });

      if (!isGetBlobData(blobData)) {
        showTypeError(API_CODE.BLOB_DATA);
        return;
      } else if (blobData.length === 0) {
        showClientError('ERR_DOWNLOADING_IMAGE');
        return;
      }

      const fileData = blobData[0].data;
      const fileType = blobData[0].path.split('/')[1];
      const fileName = blobData[0].path.split('/')[2];

      switch (fileType) {
        case 'image0':
          files[0].file = base64ToFile(fileData, fileName);
          files[0].fileName = fileName;
          break;
        case 'image1':
          files[1].file = base64ToFile(fileData, fileName);
          files[1].fileName = fileName;
          break;
        case 'document0':
          files[2].file = new File(
            [Buffer.from(fileData, 'base64').toString()],
            fileName,
          );
          files[2].fileName = fileName;
          break;
        case 'document1':
          files[3].file = new File(
            [Buffer.from(fileData, 'base64').toString()],
            fileName,
          );
          files[3].fileName = fileName;
          break;
      }
      updateFiles();
    } catch (error) {
      if (error instanceof CustomError) {
        error.data = [t('LBL_UPLOAD_FILE')];
        showApiError(error, API_CODE.BLOB_DATA);
      } else {
        showApiError(error, API_CODE.BLOB_DATA);
      }
      return;
    }
  };

  const updateFiles = () => {
    setFiles((prevFiles) => {
      return [...prevFiles];
    });
  };

  const setFile = (index: number, file?: File) => {
    if (!repairRequest) {
      return;
    }
    if (
      (files[index].isImage &&
        (!file || ACCEPT_TYPE_IMAGE.split(', ').includes(file.type))) ||
      !files[index].isImage
    ) {
      if (
        file &&
        files.some(({ fileName }, i) => i !== index && fileName === file.name)
      ) {
        showClientError(t('ERR_FILE_NAME_EXIST'));
        return;
      }
      files[index].file = file;
      files[index].fileName = file?.name;
      if (files[index].isImage) {
        dispatch(
          setFilePath({
            id: files[index].id,
            path: file
              ? repairRequest.rmaNo +
                '/image' +
                files[index].id +
                '/' +
                file.name
              : '',
          }),
        );
        if (!file) {
          dispatch(setDocumentComment({ id: files[index].id, comment: '' }));
        }
      } else {
        dispatch(
          setDocumentPath({
            id: files[index].id,
            path: file
              ? repairRequest.rmaNo +
                '/document' +
                files[index].id +
                '/' +
                file.name
              : '',
          }),
        );
      }
    }
    updateFiles();
  };

  /** 次へボタンの活性条件 */
  const checkIsRequiredInputComplete = () => {
    // 読み込み中
    if (confirmedInspection.rmaNo === '') {
      return false;
    }

    const { questions, repairType, serviceType } = confirmedInspection;
    const { after } = confirmedInspection.pfrCodeList;

    return (
      // 質問が全て回答済
      questions.every(
        (item) =>
          item.answer === ANSWER_VALUE.YES || item.answer === ANSWER_VALUE.NO,
      ) &&
      // 修理タイプが選択済み
      !isEmptyData(repairType) &&
      // サービスタイプが選択済み
      !isEmptyData(serviceType) &&
      // 顧客指摘のいずれかをチェック、またはトグルがOFF
      (after.some((row) => row.reportedByCustomer) || !isReported) &&
      // C/Dコードが全て選択済み
      after.every((row) => !isEmptyData(row.cd.code)) &&
      // Rコードが全て選択済み(念のためRコードが選択不可の場合をケア)
      after.every((row) => !isEmptyData(row.r.code) || row.rList.length === 0)
    );
  };

  /** 戻るボタン押下時 */
  const onClickBack = async () => {
    if (!repairRequest) {
      return;
    }
    // ステータスを戻す
    const isSuccess = await updateStatus(
      INSPECTION_STATUSES.INSPECTION_INPUT_RESULT,
      0,
      showApiError,
    );
    if (!isSuccess) {
      return;
    }
    resetInspectionStore();
    navigate(INSPEX_PATHNAME.INSPECTION_INPUT_RESULT);
  };

  /** 次へボタン押下時 */
  const onClickNext = async () => {
    if (!repairRequest) {
      return;
    }
    // 検査結果メモの文字数チェック
    if (
      confirmedInspection.evaluationResults.length >
      MaxLength.ConfirmedInspection.EVALUATION_RESULT
    ) {
      showClientError(
        t('ERR_INPUT_TEXT', {
          0: t('LBL_EVALUATION_RESULT'),
          1: MaxLength.ConfirmedInspection.EVALUATION_RESULT,
        }),
      );
      return;
    }

    // ファイルアップロード
    setIsLoading(true);
    const isUploaded = await uploadFileInChunks();
    setIsLoading(false);
    if (!isUploaded) {
      return;
    }

    // 確定診断情報の保存
    const putConfirmedInspection = createPutConfirmInspection();
    const response = await dispatch(
      updateConfirmedInspection(putConfirmedInspection),
    );
    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.CONFIRMED_INSPECTION);
      return;
    }

    // ステータス更新
    const isSuccess = await updateStatus(
      INSPECTION_STATUSES.INSPECTION_PARTS_SELECT,
      0,
      showApiError,
    );
    if (!isSuccess) {
      return;
    }

    // 操作履歴(確定診断のSave)を保存
    saveHistory(
      repairRequest,
      HISTORY_STATUSES.INSPECTION_CONFIRM.id,
      HISTORY_TYPE.SAVE.id,
      () => navigate(INSPEX_PATHNAME.PARTS_SELECTION),
      showApiError,
      JSON.stringify(confirmedInspection),
    );
  };

  async function uploadFileInChunks() {
    if (!repairRequest) {
      return false;
    }

    const _getBlobPath = (fileName: string): string => {
      const rmaNo = repairRequest ? repairRequest.rmaNo : '';
      // NOTE: 修理番号/ファイル名(拡張子あり)
      return `${rmaNo}/${fileName}`;
    };

    try {
      // ファイル削除
      await deleteFiles(DIAGNOSTIC_CONTAINER_NAME, repairRequest.rmaNo);
    } catch (error) {
      showApiError(error, API_CODE.DELETE_FILES);
      return false;
    }

    // 1ファイルずつファイルデータを分割してアップロードする
    for (let i = 0; i < files.length; i++) {
      const fileInfo = files[i];
      const prefix = fileInfo.isImage
        ? 'image' + fileInfo.id + '/'
        : 'document' + fileInfo.id + '/';
      if (fileInfo.file && fileInfo.fileName) {
        if (fileInfo.file === undefined) continue;
        const totalChunks = Math.ceil(fileInfo.file.size / CHUNK_SIZE);
        const blockIds: string[] = [];

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, fileInfo.file.size);
          const chunk = fileInfo.file?.slice(start, end);
          const blockId = btoa(i.toString().padStart(6, '0'));
          blockIds.push(blockId);

          // ブロックデータの送信
          const formData = new FormData();
          formData.append('containerName', DIAGNOSTIC_CONTAINER_NAME);
          formData.append('filePath', _getBlobPath(prefix + fileInfo.fileName));
          formData.append('blockId', blockId);
          formData.append('fileSize', chunk.size.toString());
          formData.append('blockData', chunk);

          try {
            await uploadBlockData(formData);
          } catch (error) {
            showApiError(error, API_CODE.UPLOAD_FILE_STAGE);
            return false;
          }
        }

        // ブロックデータの結合
        const body: PostBlockList = {
          containerName: DIAGNOSTIC_CONTAINER_NAME,
          filePath: _getBlobPath(prefix + fileInfo.fileName),
          blockIdList: blockIds,
        };
        try {
          await uploadBlockList(body);
        } catch (error) {
          showApiError(error, API_CODE.UPLOAD_FILE_COMMIT);
          return false;
        }
      }
    }
    return true;
  }

  // Base64をFile型にデコードして返却
  const base64ToFile = (base64String: string, fileName: string): File => {
    const parts = base64String.split(';base64,');
    const mimeType = parts[0].split(':')[1];
    const base64Data = parts[1];

    const binaryString = atob(base64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: mimeType });
    return new File([blob], fileName, { type: mimeType });
  };

  /** GetConfirmInspectionからPutConfirmInspectionに詰め替える */
  const createPutConfirmInspection = (): PutConfirmedInspection => {
    return {
      rmaNo: confirmedInspection.rmaNo,
      questions: confirmedInspection.questions,
      repairType: confirmedInspection.repairType,
      serviceType: confirmedInspection.serviceType,
      evaluationResults: confirmedInspection.evaluationResults,
      notesToCustomer: confirmedInspection.notesToCustomer,
      documents: confirmedInspection.documents,
      pfrCodeList: confirmedInspection.pfrCodeList.after.map((row) => {
        return {
          itemId: row.itemId,
          index: row.index,
          pCode: row.p.code,
          fCode: row.f.code,
          rCode: row.r.code,
          reportedByCustomer: row.reportedByCustomer,
          cdCode: row.cd.code,
        };
      }),
    };
  };

  return (
    <React.Fragment>
      <BasicInfo />
      <Box sx={{ padding: 2 }}>
        <Grid container spacing={2}>
          <QuestionComponent questions={confirmedInspection.questions} />
          <ResultInformation
            repairType={confirmedInspection.repairType}
            repairTypeList={confirmedInspection.repairTypeList}
            serviceType={confirmedInspection.serviceType}
            serviceTypeList={confirmedInspection.serviceTypeList}
            evaluationResults={confirmedInspection.evaluationResults}
            notesToCustomer={confirmedInspection.notesToCustomer}
          />
          <Grid
            container
            spacing={2}
            size={12}
            alignItems={'center'}
            sx={{
              bgcolor: '#F2F2F2',
              margin: '20px',
              borderRadius: '4px',
              justifyContent: 'center',
            }}
          >
            {files.map((item, index) => {
              return !item.file ? (
                <Grid container size={2.9} key={`upload_documents_${index}`}>
                  <AcceptUploadDocuments
                    key={`${item.id}_${index}`}
                    index={index}
                    isImage={item.isImage}
                    setFile={setFile}
                  />
                </Grid>
              ) : (
                <Grid container size={2.9} key={`display_documents_${index}`}>
                  <Grid
                    container
                    size={12}
                    spacing={2}
                    sx={gridStyle}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {item.isImage ? (
                      <DisplayDocuments
                        files={files}
                        index={index}
                        setFile={setFile}
                        documents={confirmedInspection.documents}
                      />
                    ) : (
                      <UploadDocuments
                        files={files}
                        index={index}
                        setDocument={setFile}
                        documents={confirmedInspection.documents}
                      />
                    )}
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
          <Grid size={12}>
            <PFRCodeTable
              pfrCodeList={confirmedInspection.pfrCodeList}
              isReported={isReported}
              setIsReported={setIsReported}
            />
          </Grid>
          <FooterButton
            isDisabledNextBtn={!checkIsRequiredInputComplete()}
            onClickBack={onClickBack}
            onClickNext={onClickNext}
          />
        </Grid>
      </Box>
      {error && <ErrorModal message={error} onClose={hideError} />}
      <Backdrop
        sx={{
          ...LOADING_BACKDROP_STYLE,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </React.Fragment>
  );
};

export default ConfirmedInspection;
